import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faMemo, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePlanOverageUsageData } from './usePlanOverageUsageData';
import { updateAccountBillingSubscription } from 'api/subscriptions';
import { SubscriptionPlanLineItemsDetailed } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionPlanLineItemsDetailed';
import { useSubscriptionPlans } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionPlans';
import * as Dialog from 'components/dialog';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { usePosthog } from 'customHooks/usePosthog';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { SubscriptionBillingPeriod, SubscriptionTier } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const UpdateToRecommendedPlanButton = () => {
    const { validGivebutterPlusPlan, accountId } = useCurrentAccount();
    const { isGracePeriod } = usePlanOverageUsageData();
    const { data: plans } = useSubscriptionPlans();
    const { success, error } = useToast();
    const { key, forceRender } = useForceRender();
    const { pushEvent } = usePosthog();

    const recommendedPlan = useMemo(() => plans.find(plan => plan.is_recommend), [plans]);

    const { mutate: updateAccount, isPending } = useMutation({
        mutationFn: () =>
            updateAccountBillingSubscription(accountId, {
                tier: recommendedPlan?.id as SubscriptionTier,
                billing_period: tier?.interval as SubscriptionBillingPeriod,
            }),
        onSuccess: () => {
            success('Plan successfully updated!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unknown error occurred');
        },
    });

    if (!validGivebutterPlusPlan || !recommendedPlan) {
        return null;
    }

    const { tier } = validGivebutterPlusPlan;

    return (
        <Dialog.Root key={key}>
            <Dialog.Trigger asChild>
                <Button
                    variant="none"
                    color="primary"
                    onClick={() => {
                        pushEvent(posthogEvents.DASHBOARD_OVERAGES_BILLING_BANNER_SELECTED, {
                            isGracePeriod,
                            text: 'Update',
                            planId: recommendedPlan.id,
                            billingCycle: tier.interval,
                        });
                    }}
                >
                    Update
                </Button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="flex max-w-sm flex-col gap-2">
                    <Dialog.Header>
                        <div className="relative flex aspect-square h-8 items-center justify-center rounded-full text-white after:absolute after:-inset-1 after:-z-[1] after:rounded-full after:bg-gray-100 after:p-2">
                            <FontAwesomeIcon icon={faMemo} className="text-gray-400" />
                        </div>

                        <Dialog.Close />
                    </Dialog.Header>
                    <Dialog.Body className="gap-6">
                        <div className="flex flex-col gap-2">
                            <Dialog.Title className="text-xl sm:text-xl">Update plan?</Dialog.Title>
                            <Dialog.Description>
                                {tier.interval === 'year'
                                    ? 'If you subscribe to an annual plan, you will be billed immediately for the full cost of your annual subscription less a prorated credit for your current subscription period.'
                                    : 'You will be billed immediately for the prorated difference between your current and existing plan.'}
                            </Dialog.Description>
                        </div>
                        <SubscriptionPlanLineItemsDetailed
                            plan={recommendedPlan}
                            showAnnualPricing={tier.interval === 'year'}
                        />
                        <div className="flex w-full gap-2">
                            <Dialog.Cancel asChild>
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    className="flex-grow"
                                    onClick={() => {
                                        pushEvent(
                                            posthogEvents.DASHBOARD_OVERAGES_BILLING_BANNER_CANCELLED,
                                            {
                                                isGracePeriod,
                                                planId: recommendedPlan.id,
                                                billingCycle: tier.interval,
                                            },
                                        );
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Dialog.Cancel>
                            <Button
                                variant="solid"
                                className="flex-grow"
                                onClick={() => {
                                    updateAccount();
                                    pushEvent(
                                        posthogEvents.DASHBOARD_OVERAGES_BILLING_BANNER_SUBMITTED,
                                        {
                                            isGracePeriod,
                                            planId: recommendedPlan.id,
                                        },
                                    );
                                }}
                                disabled={isPending}
                            >
                                {isPending && (
                                    <FontAwesomeIcon
                                        icon={faSpinnerThird}
                                        className="animate-spin"
                                    />
                                )}
                                Update
                            </Button>
                        </div>
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export { UpdateToRecommendedPlanButton };
